import React, { useState, useEffect } from 'react';
import { fetchTeamInfo } from '../services/dashboardService';
import {
  FiUsers, FiTool, FiFileText, FiClock, FiChevronDown, FiChevronRight, FiAlertTriangle, FiCheckSquare,
} from 'react-icons/fi';

const ABSENCE_ICONS = {
  ferie: '🏖️',
  syk: '🤒',
  permission_paid: '💰',
  permission_unpaid: '📅',
};

const TeamInfoPanel = ({ token, onOpenWidget, refreshTrigger = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    members: false,
    machines: false,
    orders: false,
    delayed: false,
    followup: false,
    absences: false,
  });

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchTeamInfo(token)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Kunne ikke hente team-info'))
      .finally(() => setLoading(false));
  }, [token]);

  // Re-fetch when refreshTrigger changes (triggered by socket events from MainForm)
  useEffect(() => {
    if (!token || refreshTrigger === 0) return;
    fetchTeamInfo(token)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Kunne ikke hente team-info'));
  }, [token, refreshTrigger]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="tw-px-4 tw-py-6 tw-text-center">
        <p className="tw-text-sm tw-text-slate-400 tw-m-0">Laster team-info...</p>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (!data || data.teams.length === 0) {
    return (
      <div className="tw-px-4 tw-py-6 tw-text-center">
        <p className="tw-text-sm tw-text-slate-400 tw-m-0">Du er ikke medlem av noe team.</p>
      </div>
    );
  }

  const teamNames = data.teams.map(t => t.name).join(', ');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return 'I dag';
    if (diff === 1) return 'I morgen';
    if (diff < 0) return `${Math.abs(diff)}d forsinket`;
    if (diff <= 7) return `${diff}d`;
    return d.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short' });
  };

  const renderSection = (key, icon, title, count, children, glow) => {
    const expanded = expandedSections[key];
    const shouldGlow = glow && count > 0;
    const glowStyle = shouldGlow ? {
      animation: 'teamInfoGlowBlink 2s ease-in-out infinite',
      borderColor: '#fca5a5',
    } : {};
    return (
      <div className={`tw-bg-white tw-rounded-xl tw-border tw-border-slate-200/80 tw-shadow-sm tw-overflow-hidden${shouldGlow ? ' ti-glow' : ''}`} style={glowStyle}>
        <button
          type="button"
          onClick={() => toggleSection(key)}
          className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-3 tw-text-left tw-bg-slate-50/80 tw-border-b tw-border-slate-100 tw-cursor-pointer hover:tw-bg-slate-50 tw-transition-colors"
        >
          {expanded ? <FiChevronDown size={14} className="tw-text-slate-400" /> : <FiChevronRight size={14} className="tw-text-slate-400" />}
          <span className="tw-text-slate-500">{icon}</span>
          <h4 className="tw-m-0 tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wide tw-flex-1">{title}</h4>
          <span className="tw-text-[11px] tw-font-bold tw-text-red-600 tw-bg-red-50 tw-px-2 tw-py-0.5 tw-rounded-full">{count}</span>
        </button>
        {expanded && (
          <div className="tw-p-3 tw-max-h-[280px] tw-overflow-y-auto">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tw-mb-6">
      <style>{`
        @keyframes teamInfoGlowBlink {
          0%, 100% { box-shadow: 0 0 4px 1px rgba(239, 68, 68, 0.2); }
          50% { box-shadow: 0 0 12px 4px rgba(239, 68, 68, 0.5); }
        }
      `}</style>
      {/* Section header */}
      <div className="tw-flex tw-items-center tw-gap-2.5 tw-mb-2.5 tw-pb-2 tw-border-b tw-border-slate-200/70">
        <FiUsers size={16} className="tw-text-slate-400" />
        <h3 className="tw-m-0 tw-text-[13px] tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wide">Mitt Team</h3>
        <span className="tw-text-[11px] tw-text-slate-400 tw-ml-1 tw-font-normal tw-normal-case">{teamNames}</span>
      </div>

      {/* 2-column grid */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        {/* Members */}
        {renderSection('members', <FiUsers size={14} />, 'Medlemmer', data.members.length,
          data.members.length === 0 ? (
            <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-py-2">Ingen medlemmer funnet</p>
          ) : (
            <div className="tw-space-y-1.5">
              {data.members.map(m => (
                <div key={m.id} className="tw-flex tw-items-center tw-gap-2.5 tw-py-1.5 tw-px-2 tw-rounded-lg hover:tw-bg-slate-50 tw-transition-colors">
                  <div className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-text-[10px] tw-font-bold tw-flex-shrink-0" style={{ background: 'linear-gradient(135deg, #60a5fa, #3b82f6)' }}>
                    {m.firstName?.charAt(0)}{m.lastName?.charAt(0)}
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-text-xs tw-font-semibold tw-text-slate-800 tw-truncate">{m.firstName} {m.lastName}</div>
                    {m.roleName && <div className="tw-text-[10px] tw-text-slate-400">{m.roleName}</div>}
                  </div>
                  {m.phone && (
                    <a href={`tel:${m.phone}`} className="tw-text-[10px] tw-text-blue-500 hover:tw-underline tw-flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {m.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {/* Machines */}
        {renderSection('machines', <FiTool size={14} />, 'Maskiner', data.machines.length,
          data.machines.length === 0 ? (
            <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-py-2">Ingen maskiner tilknyttet</p>
          ) : (
            <div className="tw-grid tw-grid-cols-2 tw-gap-1.5">
              {data.machines.map(m => {
                const team = data.teams.find(t => t.id === m.teamId);
                return (
                  <div key={m.id} className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-2 tw-rounded-lg tw-bg-slate-50 tw-border tw-border-slate-100">
                    <FiTool size={12} className="tw-text-orange-500 tw-flex-shrink-0" />
                    <span className="tw-text-xs tw-font-medium tw-text-slate-700 tw-truncate">{m.name}</span>
                    {team && <span className="tw-text-[9px] tw-text-slate-400 tw-flex-shrink-0">{team.name}</span>}
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* Active Orders */}
        {renderSection('orders', <FiFileText size={14} />, 'Aktive ordre', data.activeOrders.length,
          data.activeOrders.length === 0 ? (
            <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-py-2">Ingen aktive ordre for ditt team</p>
          ) : (
            <div className="tw-space-y-1">
              {data.activeOrders.map(o => {
                const team = data.teams.find(t => t.id === o.teamId);
                const dateLabel = formatDate(o.deliveryDate);
                const isOverdue = o.deliveryDate && new Date(o.deliveryDate) < new Date(new Date().toISOString().slice(0, 10));
                return (
                  <button
                    key={`${o.id}-${o.teamId}`}
                    type="button"
                    onClick={() => {
                      if (onOpenWidget) {
                        sessionStorage.setItem('selectedWorkOrderId', String(o.id));
                        onOpenWidget('ordersFlow');
                      }
                    }}
                    className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-2.5 tw-py-2 tw-rounded-lg tw-text-left tw-border-none tw-bg-transparent tw-cursor-pointer hover:tw-bg-slate-50 tw-transition-colors"
                  >
                    <span className="tw-text-[11px] tw-font-bold tw-text-indigo-600 tw-w-16 tw-flex-shrink-0 tw-truncate">{o.orderNumber}</span>
                    <span className="tw-text-[11px] tw-text-slate-700 tw-truncate tw-flex-1">{o.customer}</span>
                    {o.isStopped && <span className="tw-text-[9px] tw-font-bold tw-text-red-600 tw-bg-red-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-flex-shrink-0">Stoppet</span>}
                    {o.teamStatus && !o.isStopped && (
                      <span
                        className="tw-text-[9px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-flex-shrink-0 tw-truncate tw-max-w-[70px]"
                        style={{
                          background: o.teamStatusColor ? `${o.teamStatusColor}20` : '#f0fdf4',
                          color: o.teamStatusColor || '#15803d',
                        }}
                      >
                        {o.teamStatus}
                      </span>
                    )}
                    <span className={`tw-text-[10px] tw-font-semibold tw-flex-shrink-0 ${isOverdue ? 'tw-text-red-500' : 'tw-text-slate-400'}`}>{dateLabel}</span>
                  </button>
                );
              })}
            </div>
          )
        )}

        {/* Delayed Orders */}
        {renderSection('delayed', <FiAlertTriangle size={14} />, 'Forsinkede ordrer', (data.delayedOrders || []).length,
          (data.delayedOrders || []).length === 0 ? (
            <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-py-2">Ingen forsinkede ordrer ✓</p>
          ) : (
            <div className="tw-space-y-1">
              {(data.delayedOrders || []).map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    if (onOpenWidget) {
                      sessionStorage.setItem('selectedWorkOrderId', String(o.id));
                      onOpenWidget('ordersFlow');
                    }
                  }}
                  className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-2.5 tw-py-2 tw-rounded-lg tw-text-left tw-border-none tw-bg-transparent tw-cursor-pointer hover:tw-bg-red-50 tw-transition-colors"
                >
                  <span className="tw-text-[11px] tw-font-bold tw-text-red-600 tw-w-16 tw-flex-shrink-0 tw-truncate">{o.orderNumber}</span>
                  <span className="tw-text-[11px] tw-text-slate-700 tw-truncate tw-flex-1">{o.customer}</span>
                  {o.isStopped && <span className="tw-text-[9px] tw-font-bold tw-text-red-600 tw-bg-red-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-flex-shrink-0">Stoppet</span>}
                  <span className="tw-text-[10px] tw-font-bold tw-text-red-500 tw-flex-shrink-0">{o.daysOverdue}d</span>
                </button>
              ))}
            </div>
          )
        , true)}

        {/* Order Follow-up (for selger / order creator) */}
        {(data.orderFollowup || []).length > 0 &&
          renderSection('followup', <FiCheckSquare size={14} />, 'Ordre Oppfølging', (data.orderFollowup || []).length,
            (<div className="tw-space-y-2">
              {data.orderFollowup.map(o => {
                const cp = o.checkpoints || {};
                const allDone = cp.timer && cp.raporter && cp.innkjop && cp.frakt && cp.klar;
                const doneCount = [cp.timer, cp.raporter, cp.innkjop, cp.frakt, cp.klar].filter(Boolean).length;
                const checkpointItems = [
                  { key: 'timer', label: '⏱️', checked: cp.timer },
                  { key: 'raporter', label: '📋', checked: cp.raporter },
                  { key: 'innkjop', label: '🛒', checked: cp.innkjop },
                  { key: 'frakt', label: '🚚', checked: cp.frakt },
                  { key: 'klar', label: '✅', checked: cp.klar },
                ];
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      if (onOpenWidget) {
                        sessionStorage.setItem('selectedWorkOrderId', String(o.id));
                        onOpenWidget('ordersFlow');
                      }
                    }}
                    className="tw-flex tw-flex-col tw-w-full tw-px-2.5 tw-py-2 tw-rounded-lg tw-text-left tw-border-none tw-bg-transparent tw-cursor-pointer hover:tw-bg-slate-50 tw-transition-colors"
                  >
                    <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-mb-1">
                      <span className="tw-text-[11px] tw-font-bold tw-text-indigo-600 tw-w-16 tw-flex-shrink-0 tw-truncate">{o.orderNumber}</span>
                      <span className="tw-text-[11px] tw-text-slate-700 tw-truncate tw-flex-1">{o.customer}</span>
                      <span className={`tw-text-[9px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded tw-flex-shrink-0 ${allDone ? 'tw-text-emerald-700 tw-bg-emerald-100' : 'tw-text-amber-700 tw-bg-amber-100'}`}>
                        {doneCount}/5
                      </span>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-1">
                      {checkpointItems.map(item => (
                        <span
                          key={item.key}
                          className="tw-flex tw-items-center tw-gap-0.5 tw-text-[9px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded"
                          style={{
                            background: item.checked ? '#d1fae5' : '#f3f4f6',
                            color: item.checked ? '#065f46' : '#94a3b8',
                          }}
                        >
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          )
        , true)
        }
        {renderSection('absences', <FiClock size={14} />, 'Fravær i dag', (data.absencesToday || []).length,
          data.absencesToday.length === 0 ? (
            <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-py-2">Ingen fravær i dag ✓</p>
          ) : (
            <div className="tw-space-y-1.5">
              {data.absencesToday.map((a, i) => (
                <div key={`${a.employeeId}-${i}`} className="tw-flex tw-items-center tw-gap-2.5 tw-py-1.5 tw-px-2 tw-rounded-lg hover:tw-bg-slate-50 tw-transition-colors">
                  <span className="tw-text-base tw-flex-shrink-0">{ABSENCE_ICONS[a.type] || '📋'}</span>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-text-xs tw-font-semibold tw-text-slate-800 tw-truncate">{a.name}</div>
                    <div className="tw-text-[10px] tw-text-slate-400">{a.typeLabel}{a.note ? ` — ${a.note}` : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )
        , true)}
      </div>
    </div>
  );
};

export default TeamInfoPanel;
